import { CrossFrameAPI } from 'scorm-again';
import { logLmsLanguage } from './learnerLanguagePreference';
import {
  ScormVersion,
  useSequenceStore
} from '~/training/sequence/SequenceStore';
import { getParentWindow, shouldUseParentHost } from './host';
import { InteractionArgs, LmsAdapter } from './types';

/**
 * SCORM API surface that the learner SCO talks to. We don't depend on
 * `scorm-again`'s `Scorm12API` / `Scorm2004API` directly because those are
 * the LMS-side implementations. On the content side we go through
 * `CrossFrameAPI`, which is a `Proxy` that mimics the SCORM API and
 * forwards every call to a `CrossFrameLMS` running in the parent frame
 * over `postMessage`.
 *
 * Why CrossFrameAPI instead of `window.parent.API_*` discovery: the SCORM
 * package launcher (uploaded to SCORM Cloud / customer LMS) and our learner
 * UI live on different origins (`cloud.scorm.com` ↔ `app.easyllama.com` in
 * production, or `cloud.scorm.com` ↔ a feature-branch subdomain in QA).
 * Same-Origin Policy forbids reading `window.parent.API_1484_11` across
 * origins, so the standard SCORM discovery walk would always fail. The
 * cross-frame bridge sidesteps this exactly the way RXD does, with
 * `postMessage`. This requires the launcher to instantiate `CrossFrameLMS`
 * on its side; that's a separate change in the SCORM packaging service.
 */
interface Scorm12Like {
  LMSInitialize(arg: string): string;
  LMSFinish(arg: string): string;
  LMSGetValue(element: string): string;
  LMSSetValue(element: string, value: unknown): string;
  LMSCommit(arg: string): string;
}

interface Scorm2004Like {
  Initialize(arg: string): string;
  Terminate(arg: string): string;
  GetValue(element: string): string;
  SetValue(element: string, value: unknown): string;
  Commit(arg: string): string;
}

type ScormApi = Scorm12Like & Scorm2004Like;

interface CrossFrameApiInstance extends ScormApi {
  destroy(): void;
}

const SCORM_VERSION_STORAGE_KEY = 'easyllama.scormAgain.version';

/**
 * Reads the `scorm_version` marker from query string OR hash fragment.
 *
 * Why both? Some hosts (the EasyLlama redirect chain
 * `l.easyllama.com → testscorm.easyllama.com`) drop the original query
 * string and replace it with their own (`?token=…`). The hash fragment
 * survives HTTP redirects when the redirect target has no fragment, so we
 * piggyback on it. The launcher writes the marker into both places.
 */
function readScormVersionMarker(): ScormVersion | undefined {
  if (typeof window === 'undefined') return undefined;
  const haystacks: string[] = [];
  try {
    if (window.location?.search) haystacks.push(window.location.search);
  } catch {
    /* noop */
  }
  try {
    if (window.location?.hash) {
      // hash starts with '#'; URLSearchParams wants '?...' so strip it.
      haystacks.push(window.location.hash.replace(/^#/, '?'));
    }
  } catch {
    /* noop */
  }
  for (const h of haystacks) {
    try {
      const v = new URLSearchParams(h).get('scorm_version');
      if (v === '1.2' || v === '2004') return v;
    } catch {
      /* noop */
    }
  }
  return undefined;
}

/**
 * Cross-frame SCORM bridge: the launcher (in the SCORM package) tells us
 * which LMS API it talked to via `?scorm_version=1.2|2004` on our iframe URL
 * (also mirrored to `#scorm_version=…` to survive redirects that strip the
 * query string). Falls back to the sequence store, then to '2004' as a last
 * resort. Reading the wrong version makes us call e.g. `SetValue` when the
 * LMS only exposes `LMSSetValue`, which the bridge rejects with
 * "Method SetValue not found".
 */
function readScormVersion(): ScormVersion {
  const fromUrl = readScormVersionMarker();
  if (fromUrl) return fromUrl;
  try {
    const fromStorage = window.sessionStorage?.getItem(
      SCORM_VERSION_STORAGE_KEY
    );
    if (fromStorage === '1.2' || fromStorage === '2004') return fromStorage;
  } catch {
    /* noop */
  }
  try {
    return useSequenceStore.getState().scormVersion ?? '2004';
  } catch {
    return '2004';
  }
}

function isInteractionsEnabled(): boolean {
  try {
    return useSequenceStore.getState().sendScormAgainInteractions !== false;
  } catch {
    return true;
  }
}

function stringifyResponse(value: unknown): string {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map(v => stringifyResponse(v)).join(',');
  }
  if (typeof value === 'boolean') {
    return value ? 'true' : 'false';
  }
  return String(value);
}

function toResult(success: unknown): 'correct' | 'incorrect' {
  return success ? 'correct' : 'incorrect';
}

interface InteractionShape {
  id: string;
  response: string;
  result: 'correct' | 'incorrect';
  correctResponse: string;
  description: string;
}

function normalizeInteractionArgs(args: InteractionArgs): InteractionShape {
  return {
    id: stringifyResponse(args[0]),
    response: stringifyResponse(args[1]),
    result: toResult(args[2]),
    correctResponse: stringifyResponse(args[3]),
    description: args[4] != null ? stringifyResponse(args[4]) : ''
  };
}

/**
 * Treats `value` as a comma-separated list of identifiers (the SCORM 2004
 * `CMI_Set_Of_Identifiers` / `CMI_String_Array` format) and returns true
 * only if every item is a CMIIdentifier and the comma is the *only*
 * separator (no surrounding whitespace).
 *
 * We deliberately do NOT trim per-part: `chooseInteractionType` uses the
 * result to decide whether the response can be sent verbatim. If we
 * accepted "optA, optB" here, `setValue('cmi.interactions.n.learner_response',
 * 'optA, optB')` would still go out with the leading space in front of
 * `optB` and SCORM Cloud rejects it (CMIIdentifier doesn't allow spaces).
 * Mirrors the strict whole-string regex of {@link isScorm12ChoiceFeedbackSafe}.
 */
function isCmiIdentifierList(value: string): boolean {
  if (!value) return true;
  return /^[A-Za-z0-9_\-+.]+(,[A-Za-z0-9_\-+.]+)*$/.test(value);
}

/**
 * SCORM 1.2 RTE §3.4.6 defines `CMIFeedback` for choice / sequencing
 * interactions as "alphanumeric identifiers, comma-separated" — i.e.
 * `[A-Za-z0-9]+(,[A-Za-z0-9]+)*`. No spaces, punctuation, or extended
 * characters. The validator that ships with `scorm-again` and SCORM
 * Cloud's own Engine reject anything outside that grammar with
 * `error 405`.
 */
function isScorm12ChoiceFeedbackSafe(value: string): boolean {
  if (!value) return true;
  return /^[A-Za-z0-9]+(,[A-Za-z0-9]+)*$/.test(value);
}

/** SCORM 1.2 true-false vocabulary (case-insensitive). */
function isScorm12TrueFalseValue(value: string): boolean {
  const v = value.trim().toLowerCase();
  return v === 'true' || v === 'false' || v === 't' || v === 'f';
}

/**
 * Normalizes a learner / correct response for `type='true-false'` to the
 * exact tokens SCORM accepts (`'true'` / `'false'`, lowercase).
 *
 * SCORM 2004 RTE §4.2.4 is strict: `learner_response` for true-false must
 * be exactly `"true"` or `"false"`. Anything else — including `"True"`,
 * `"False"`, `"t"`, `"1"` — is rejected with error 406. Our consumer code
 * historically sent stringified booleans through several layers, some of
 * which capitalize (`String.prototype` does not, but JSX-driven helpers
 * sometimes did), and that's exactly what we observed in SCORM Cloud
 * logs (`SetValue('cmi.interactions.6.learner_response', 'False') → 406`).
 *
 * We accept the same broad input vocabulary as
 * {@link isScorm12TrueFalseValue} (`true`/`false`/`t`/`f`/`1`/`0`) and
 * collapse it to the canonical lowercase tokens. Anything we cannot
 * confidently classify is returned untouched — but in practice this
 * helper is only called after `chooseInteractionType` has already
 * confirmed the value belongs to the TF vocabulary, so the fallback
 * branch is unreachable in normal flows.
 */
function normalizeTrueFalse(value: string): string {
  const v = value.trim().toLowerCase();
  if (v === 'true' || v === 't' || v === '1') return 'true';
  if (v === 'false' || v === 'f' || v === '0') return 'false';
  return value;
}

/** SCORM 1.2 `CMIString255`: any string up to 255 characters. */
const SCORM_12_FILL_IN_MAX_LEN = 255;

function clampToFillIn(value: string): string {
  if (value.length <= SCORM_12_FILL_IN_MAX_LEN) return value;
  return value.slice(0, SCORM_12_FILL_IN_MAX_LEN);
}

class ScormAgainBackend {
  private initialized = false;
  /** After Terminate / concedeControl — never Initialize or SetValue again (SCORM 104/133). */
  private sessionEnded = false;
  private interactionIndex = 0;

  constructor(
    /** SCORM version negotiated with the launcher; exposed for diagnostics. */
    readonly version: ScormVersion,
    private readonly api: CrossFrameApiInstance
  ) {}

  destroy(): void {
    try {
      this.api.destroy();
    } catch (error) {
      console.error('[lms/scorm-again] CrossFrameAPI destroy failed:', error);
    }
  }

  private safe<T>(fn: () => T): T | undefined {
    try {
      return fn();
    } catch (error) {
      console.error('[lms/scorm-again] call failed:', error);
      return undefined;
    }
  }

  ensureInitialized(): boolean {
    if (this.sessionEnded) return false;
    if (this.initialized) return true;
    const result = this.safe(() => {
      if (this.version === '2004') {
        return this.api.Initialize('');
      }
      return this.api.LMSInitialize('');
    });
    // CrossFrameAPI returns "true" synchronously and dispatches the real
    // call asynchronously over postMessage. Treating "true"/"false" alike
    // matches our prior behavior — many LMSes reply "false" on a second
    // Initialize but subsequent SetValue calls still go through.
    this.initialized = result === 'true' || result === 'false';
    return this.initialized;
  }

  /**
   * Establish a SCORM session immediately, without waiting for the first
   * `setValue`. Called once when the backend is first instantiated. We mirror
   * the well-known bootstrap pattern from Rustici's RXD driver:
   *
   *   - `LMSInitialize('')` / `Initialize('')` — start session.
   *   - `cmi.core.exit='suspend'` (1.2) / `cmi.exit='suspend'` (2004) — so
   *     a tab close mid-course suspends instead of terminating; the user's
   *     state is preserved when they re-open the SCO.
   *   - `cmi.core.lesson_status='incomplete'` (1.2) /
   *     `cmi.completion_status='incomplete'` (2004) — tell the LMS the
   *     SCO is in progress.
   *
   * Why the explicit `incomplete` write?
   *
   * SCORM 1.2 RTE §3.4.1: `cmi.core.lesson_status` defaults to
   * `not attempted`. Per spec, when a session closes with `not attempted`
   * AND no score, the LMS runs its session-end fallback and auto-defaults
   * the status to `completed`. SCORM Cloud's Close Out Session log spells
   * this out:
   *
   *     Current status is not attempted so changing based on score
   *     Missing mastery score or raw score, setting to completed
   *     Sco is completed so resetting credit to no-credit and mode to review
   *
   * So a learner who closes the tab after one screen ends up with a
   * "completed" course on their record. Writing `incomplete` once on
   * bootstrap claims the status for us; `setReachedEnd` / `setPassed`
   * then overwrite it when the learner actually finishes.
   *
   * For SCORM 2004 our manifest already declares
   * `completionSetByContent="true"`, so a 2004 LMS should not auto-default
   * the field. We still write `incomplete` for symmetry with 1.2 and to
   * cover LMSes that ignore the manifest hint.
   *
   * Without this, SCORM 1.2 sessions never call `LMSInitialize` until the
   * user actually answers a quiz or reaches Success — leaving the LMS log
   * empty for users who close the tab early.
   */
  bootstrapSession(): void {
    if (!this.ensureInitialized()) return;
    if (this.version === '2004') {
      this.setValue('cmi.exit', 'suspend');
      this.setValue('cmi.completion_status', 'incomplete');
    } else {
      this.setValue('cmi.core.exit', 'suspend');
      this.setValue('cmi.core.lesson_status', 'incomplete');
    }
  }

  setValue(element: string, value: unknown): void {
    if (!this.ensureInitialized()) return;
    this.safe(() => {
      if (this.version === '2004') {
        this.api.SetValue(element, value);
      } else {
        this.api.LMSSetValue(element, value);
      }
    });
  }

  /**
   * Synchronous CMI read. `CrossFrameAPI.GetValue` returns from a local
   * cache that gets warmed by the `getFlattenedCMI` round-trip every API
   * dispatches after Initialize/Set/Commit/Finish — so an immediate read
   * after Initialize will most often see `''`, and only later calls will
   * return the real value. Callers that need the value reliably should
   * use {@link getLearnerLanguagePreference} (or build a similar polled
   * variant) instead of relying on the first synchronous read.
   */
  private getValue(element: string): string {
    if (!this.ensureInitialized()) return '';
    const result = this.safe(() => {
      if (this.version === '2004') {
        return this.api.GetValue(element);
      }
      return this.api.LMSGetValue(element);
    });
    return typeof result === 'string' ? result : '';
  }

  /**
   * Await the real LMS response for a CMI read. `LMSGetValue` on
   * `CrossFrameAPI` returns the local cache immediately (often `''`) and
   * fills it only after the postMessage round-trip — CES logs show `fr`
   * on the parent side while our sync cache read still looks empty.
   */
  private async getValueAsync(element: string): Promise<string> {
    if (!this.ensureInitialized()) {
      logLmsLanguage('scorm read skipped (not initialized)', {
        element,
        version: this.version
      });
      return '';
    }
    const bridge = this.api as CrossFrameApiInstance & {
      _post?: (method: string, params: unknown[]) => Promise<unknown>;
    };
    const method = this.version === '2004' ? 'GetValue' : 'LMSGetValue';
    if (typeof bridge._post === 'function') {
      const result = await this.safe(() => bridge._post!(method, [element]));
      const value = typeof result === 'string' ? result.trim() : '';
      logLmsLanguage('scorm async GetValue', {
        element,
        method,
        value: value || '(empty)'
      });
      return value;
    }
    const cached = this.getValue(element).trim();
    logLmsLanguage('scorm sync cache GetValue (no _post)', {
      element,
      value: cached || '(empty)'
    });
    return cached;
  }

  /**
   * Returns the learner's language preference reported by the LMS.
   *
   * Polls with async `GetValue` round-trips until a non-empty value arrives
   * or the timeout elapses. Some hosts (e.g. Go1) populate
   * `cmi.student_preference.language` only after `USER_INFO` is processed,
   * so the first sync cache read is often still empty.
   */
  async getLearnerLanguagePreference(): Promise<string> {
    if (!this.ensureInitialized()) {
      logLmsLanguage('scorm language read skipped (not initialized)', {
        version: this.version
      });
      return '';
    }
    const key =
      this.version === '2004'
        ? 'cmi.learner_preference.language'
        : 'cmi.student_preference.language';

    logLmsLanguage('scorm language read start', { key, version: this.version });

    const POLL_INTERVAL_MS = 100;
    const MAX_WAIT_MS = 8000;
    const start = Date.now();

    while (Date.now() - start < MAX_WAIT_MS) {
      const asyncValue = await this.getValueAsync(key);
      if (asyncValue) {
        logLmsLanguage('scorm language resolved (async poll)', {
          key,
          value: asyncValue,
          waitedMs: Date.now() - start
        });
        return asyncValue;
      }

      const cachedValue = this.getValue(key).trim();
      if (cachedValue) {
        logLmsLanguage('scorm language resolved (sync cache)', {
          key,
          value: cachedValue,
          waitedMs: Date.now() - start
        });
        return cachedValue;
      }

      await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
    }

    logLmsLanguage('scorm language read timeout', {
      key,
      waitedMs: MAX_WAIT_MS
    });
    return '';
  }

  commit(): void {
    if (this.sessionEnded || !this.initialized) return;
    this.safe(() => {
      if (this.version === '2004') {
        this.api.Commit('');
      } else {
        this.api.LMSCommit('');
      }
    });
  }

  terminate(): void {
    if (this.sessionEnded) return;
    if (!this.initialized) return;
    this.safe(() => {
      if (this.version === '2004') {
        this.api.Terminate('');
      } else {
        this.api.LMSFinish('');
      }
    });
    this.initialized = false;
    this.sessionEnded = true;
  }

  setProgressMeasure(value: number): void {
    if (this.version !== '2004') return;
    const clamped = Math.max(0, Math.min(1, value));
    this.setValue('cmi.progress_measure', clamped.toString());
  }

  setScore(score: number, maxScore: number, minScore: number): void {
    if (this.version === '2004') {
      const normalizedMax = maxScore === 0 ? 100 : maxScore;
      const scaled = Math.max(
        -1,
        Math.min(1, (score - minScore) / (normalizedMax - minScore || 1))
      );
      this.setValue('cmi.score.scaled', scaled.toFixed(4));
      this.setValue('cmi.score.raw', String(score));
      this.setValue('cmi.score.min', String(minScore));
      this.setValue('cmi.score.max', String(normalizedMax));
    } else {
      this.setValue('cmi.core.score.raw', String(score));
      this.setValue('cmi.core.score.min', String(minScore));
      this.setValue('cmi.core.score.max', String(maxScore));
    }
  }

  setReachedEnd(): void {
    if (this.version === '2004') {
      this.setValue('cmi.completion_status', 'completed');
    } else {
      // SCORM 1.2 has no separate completion vs success status —
      // `setPassed` will overwrite this if it is called afterwards.
      this.setValue('cmi.core.lesson_status', 'completed');
    }
  }

  setPassed(): void {
    if (this.version === '2004') {
      this.setValue('cmi.success_status', 'passed');
    } else {
      this.setValue('cmi.core.lesson_status', 'passed');
    }
  }

  concedeControl(): void {
    // Before Terminate, do TWO things on SCORM 2004:
    //
    //   1. Clear the `cmi.exit='suspend'` flag we set during
    //      `bootstrapSession`. The bootstrap flag is what makes mid-course
    //      tab closes resumable, but at the end of the SCO it is the
    //      opposite of what we want. RTE §4.2.6 says `cmi.exit='suspend'`
    //      at Terminate keeps the SCO popup open (the LMS expects a
    //      follow-up resume). Empty string `''` ≡ "normal exit, session
    //      complete".
    //
    //   2. Issue a navigation request via `adl.nav.request='exit'`.
    //      `cmi.exit=''` alone is metadata — it tells the LMS HOW the
    //      session ended, but does not ASK the LMS to do anything. The
    //      sequencing engine (in SCORM Cloud and most modern LMSes) only
    //      tears down the activity tree and closes the popup when the
    //      content explicitly issues an exit navigation request. Without
    //      it the SCO terminates cleanly but the popup just sits there
    //      with a "completed" status — exactly what users were seeing.
    //      Setting `'exit'` (instead of the broader `'exitAll'`) is the
    //      conservative choice for our single-SCO course manifest; both
    //      are valid per ADL Nav §3.5.
    //
    // For SCORM 1.2 the field is `cmi.core.exit` and there's no analogue
    // of `adl.nav.request` — the 1.2 LMS popup closes on `LMSFinish`
    // unconditionally, so we just clear the suspend flag for consistency.
    if (this.initialized && !this.sessionEnded) {
      if (this.version === '2004') {
        this.setValue('cmi.exit', '');
        this.setValue('adl.nav.request', 'exit');
      } else {
        this.setValue('cmi.core.exit', '');
      }
    }
    this.commit();
    this.terminate();
  }

  recordInteraction(
    type: 'choice' | 'true-false' | 'sequencing' | 'fill-in',
    raw: InteractionArgs
  ): void {
    if (this.sessionEnded) return;
    if (!this.ensureInitialized()) return;
    const shape = normalizeInteractionArgs(raw);
    const idx = this.interactionIndex++;
    const prefix = `cmi.interactions.${idx}`;

    // Map our internal interaction type to a SCORM `cmi.interactions.n.type`.
    // The SCORM 2004 spec is strict about the format of `learner_response`
    // and `correct_responses.n.pattern` per type:
    //
    //   - true-false   : exactly "true" / "false"
    //   - choice       : comma-separated CMIIdentifiers (no spaces / commas)
    //   - sequencing   : comma-separated CMIIdentifiers in attempt order
    //   - fill-in      : short text (≤250 chars after normalization)
    //   - other        : free-form string up to 4000 chars (catch-all)
    //
    // Our front-end passes free-text answer labels (e.g. "Offensive
    // comments, jokes, slurs") that contain commas / spaces / punctuation
    // and are therefore rejected by SCORM Cloud's validator with
    // `SetValue(..., ...) returned 'false'`. To preserve interaction data
    // we relax `type` to `other` when the response cannot fit the strict
    // format. The semantically-meaningful question text still goes to
    // `description`, so reports stay readable. SCORM 1.2 has no `other`
    // type — it gets the same comma-stripping fallback further down.
    const scormType = this.chooseInteractionType(type, shape);

    const uniqueId = `${shape.id}_${idx}`;
    this.setValue(`${prefix}.id`, uniqueId);
    this.setValue(`${prefix}.type`, scormType);

    // For TF interactions, the response / pattern fields must be the
    // canonical `'true'` / `'false'` lowercase strings. Consumer code
    // sometimes hands us `'True'` / `'False'` / `'t'` / `'1'` etc., and
    // SCORM Cloud rejects anything else with error 406 (see
    // {@link normalizeTrueFalse}).
    const response =
      scormType === 'true-false'
        ? normalizeTrueFalse(shape.response)
        : shape.response;
    const correctResponse =
      scormType === 'true-false'
        ? normalizeTrueFalse(shape.correctResponse)
        : shape.correctResponse;

    if (this.version === '2004') {
      this.setValue(`${prefix}.learner_response`, response);
      if (correctResponse) {
        this.setValue(`${prefix}.correct_responses.0.pattern`, correctResponse);
      }
      this.setValue(`${prefix}.result`, shape.result);
      if (shape.description) {
        this.setValue(`${prefix}.description`, shape.description);
      }
    } else {
      // SCORM 1.2 specifics:
      //   - `result` vocabulary uses `wrong` (not `incorrect` like 2004),
      //     otherwise SCORM Cloud rejects with error 405.
      //   - For `fill-in` fallbacks, `student_response` and the correct
      //     pattern are `CMIString255` — clamp to 255 chars to satisfy
      //     length limits.
      //   - `cmi.interactions.n.description` is a SCORM 2004 field;
      //     many 1.2 LMSes silently ignore it. We omit it to stay
      //     strictly compliant.
      const result12 = shape.result === 'incorrect' ? 'wrong' : shape.result;

      const isFillInFallback = scormType === 'fill-in';
      const response12 = isFillInFallback ? clampToFillIn(response) : response;
      const correct12 = isFillInFallback
        ? clampToFillIn(correctResponse)
        : correctResponse;

      this.setValue(`${prefix}.student_response`, response12);
      if (correct12) {
        this.setValue(`${prefix}.correct_responses.0.pattern`, correct12);
      }
      this.setValue(`${prefix}.result`, result12);
    }
  }

  /**
   * Picks the right `cmi.interactions.n.type` for the data we have.
   *
   * The internal type from the React layer (`choice` / `true-false` /
   * `sequencing` / `fill-in`) reflects the *UI control* the user
   * interacted with — it does NOT guarantee the response shape complies
   * with SCORM's strict format for that type:
   *
   *   - SCORM 2004: choice / sequencing want `CMIIdentifier`s; true-false
   *     wants exactly "true" / "false". Free-form text is rejected. We
   *     fall back to `other`, which accepts any string up to 4000 chars.
   *
   *   - SCORM 1.2: same constraints but no `other` type. We fall back to
   *     `fill-in`, which accepts `CMIString255` (any string up to 255
   *     chars) and is the only type that tolerates free-form responses
   *     in the 1.2 spec.
   *
   * `description` is preserved either way, so the human-readable question
   * text still shows up in LMS reports regardless of the chosen type.
   */
  private chooseInteractionType(
    requested: 'choice' | 'true-false' | 'sequencing' | 'fill-in',
    shape: InteractionShape
  ): 'choice' | 'true-false' | 'sequencing' | 'fill-in' | 'other' {
    if (this.version !== '2004') {
      // SCORM 1.2 — fall back to `fill-in` (CMIString255) when the
      // response can't fit the requested type's CMIFeedback grammar.
      if (requested === 'true-false') {
        if (isScorm12TrueFalseValue(shape.response)) return 'true-false';
        return 'fill-in';
      }
      if (requested === 'choice' || requested === 'sequencing') {
        const learnerOk = isScorm12ChoiceFeedbackSafe(shape.response);
        const correctOk = isScorm12ChoiceFeedbackSafe(shape.correctResponse);
        if (learnerOk && correctOk) return requested;
        return 'fill-in';
      }
      // `fill-in` already accepts free-form strings; nothing to relax.
      return 'fill-in';
    }

    // SCORM 2004 strict-format check.
    if (requested === 'true-false') {
      const lr = shape.response.trim().toLowerCase();
      if (lr === 'true' || lr === 'false') return 'true-false';
      // Mis-classified as TF in the UI but the response is free-form —
      // bail straight to `other` (used to be `choice`, but `choice` has
      // its own strict format so the value would still be rejected).
      return 'other';
    }

    if (requested === 'choice' || requested === 'sequencing') {
      const learnerOk = isCmiIdentifierList(shape.response);
      const correctOk = isCmiIdentifierList(shape.correctResponse);
      if (learnerOk && correctOk) return requested;
      return 'other';
    }

    // SCORM 2004 4th Ed RTE §4.2.4 defines both `learner_response` and
    // `correct_responses.n.pattern` for `type='fill-in'` as
    // `CMI_String_Array` of `CMILangString250` — localized free-form text
    // up to 250 chars per match. Spaces, punctuation, mixed case, and
    // Unicode are all permitted; the *only* hard constraint is the
    // per-match length. We must check both fields, otherwise an oversized
    // correct pattern would still be `SetValue`'d as fill-in and rejected.
    // Do not gate fill-in on CMIIdentifier rules: free text (e.g. "New York City")
    // must stay `fill-in` when within length limits.
    if (requested === 'fill-in') {
      if (shape.response.length <= 250 && shape.correctResponse.length <= 250) {
        return 'fill-in';
      }
      return 'other';
    }

    return requested;
  }
}

let activeBackend: ScormAgainBackend | undefined;

function createCrossFrameApi(target: Window): CrossFrameApiInstance {
  // TODO: lock `targetOrigin` to the SCORM Cloud / customer LMS origin
  // once we have a config field for it. Wildcard works but produces a
  // console warning from scorm-again about insecure broadcast.
  const targetOrigin = '*';
  const api = new CrossFrameAPI(targetOrigin, target);
  return api as unknown as CrossFrameApiInstance;
}

const SCORM_HOST_STORAGE_KEY = 'easyllama.scormAgain.detected';

/**
 * Latched result of {@link isScormAgainHostDetected}. Once we have ever
 * positively detected that we are inside a scorm-again launcher (via the
 * `?scorm_version=` marker, the matching hash fragment, or a previously
 * persisted `sessionStorage` flag), we remember it forever.
 *
 * Why latch? The SPA does `history.replaceState` after auth to clean
 * `?token=…&scorm_version=…` out of the URL bar — perfectly normal SPA
 * behavior. Some host redirects (e.g. EasyLlama's
 * `l.easyllama.com → testscorm.easyllama.com`) replace the original query
 * string outright. If we re-checked `window.location.search` on every call,
 * the marker would disappear and `isAvailable` would silently flip to
 * `false`. That breaks `Success.tsx` (`if (lms.isAvailable) lms.setScore(…)`)
 * and `AnswerButton.tsx`, which is why scores stopped reaching the LMS in
 * the first cross-frame test run. We mirror the marker to:
 *   - module-level latch (this run);
 *   - `sessionStorage` (this tab, surviving SPA navigations and reloads);
 *   - hash fragment (`#scorm_version=…`) so it survives redirects that drop
 *     the query string.
 */
let scormAgainHostLatched = false;

/**
 * Detects whether the SPA is running inside our scorm-again launcher.
 * Detection sources, in priority order:
 *
 *   1. In-memory latch.
 *   2. `?scorm_version=` query OR `#scorm_version=` hash fragment — written
 *      by the launcher, see `_LMS_SCORM/launcher/index.template.html`.
 *   3. `sessionStorage` flag, set the first time we ever observe (2). This
 *      survives both `history.replaceState` and SPA route changes within
 *      the tab, even if the browser later drops the marker from the URL.
 *
 * Without one of these signals we don't know whether the parent frame
 * speaks the `CrossFrameLMS` postMessage protocol; instantiating
 * `CrossFrameAPI` blindly would just spam timeouts into the console
 * because no peer answers `Initialize` / `SetValue`.
 *
 * Detection is per-call (preprod parity with the `rxdAdapter` pattern),
 * but positive results latch — see {@link scormAgainHostLatched}.
 */
function isScormAgainHostDetected(): boolean {
  if (scormAgainHostLatched) return true;

  if (
    typeof window !== 'undefined' &&
    (window as Window & { __CYPRESS_COMPONENT_TEST__?: boolean })
      .__CYPRESS_COMPONENT_TEST__ === true
  ) {
    return false;
  }

  if (!shouldUseParentHost()) return false;

  const parent = getParentWindow();
  if (!parent || parent === window) return false;

  const fromUrl = readScormVersionMarker();
  if (fromUrl) {
    scormAgainHostLatched = true;
    persistDetection(fromUrl);
    return true;
  }

  try {
    const flag = window.sessionStorage?.getItem(SCORM_HOST_STORAGE_KEY);
    if (flag === '1' || flag === 'true') {
      scormAgainHostLatched = true;
      return true;
    }
  } catch {
    /* noop */
  }

  return false;
}

/**
 * Mirrors the detection result to `sessionStorage` so subsequent calls
 * after `history.replaceState` (which strips both query and hash) still
 * find a marker. Best-effort — silently ignore quota / disabled-storage
 * errors.
 */
function persistDetection(version: ScormVersion): void {
  try {
    window.sessionStorage?.setItem(SCORM_HOST_STORAGE_KEY, '1');
    window.sessionStorage?.setItem(SCORM_VERSION_STORAGE_KEY, version);
  } catch {
    /* noop */
  }
}

// Run detection eagerly on module load so we capture the marker before
// any SPA `history.replaceState` / route navigation strips it from the
// URL. We call `isScormAgainHostDetected()` (instead of inlining the URL
// check) so the same guards run as in the per-call path:
//
//   - `shouldUseParentHost()` (skip when embedded by a first-party
//     EasyLlama domain we serve ourselves);
//   - `parent !== window` (no point in CrossFrameAPI when we're top-level);
//   - `__CYPRESS_COMPONENT_TEST__` (do not auto-latch in component tests
//     that happen to mount the SPA with `?scorm_version=…` in the URL).
//
// `isScormAgainHostDetected()` itself sets the latch + sessionStorage on
// success, so we just call it for its side effect.
if (typeof window !== 'undefined') {
  try {
    isScormAgainHostDetected();
  } catch {
    /* noop */
  }
}

/**
 * Lazily creates the `CrossFrameAPI` bridge + `ScormAgainBackend` on first
 * actual use. We do NOT create them in `createScormAgainAdapter` because:
 *
 *   1. Instantiating `CrossFrameAPI` immediately attaches a global
 *      `message` event listener and starts a heartbeat. If we're inside the
 *      RXD wrapper (which doesn't speak the scorm-again protocol) those
 *      heartbeats just produce timeout noise in the console.
 *   2. Detection is per-call (`isScormAgainHostDetected`) — eager creation
 *      would force a single boot-time decision which is exactly what we
 *      removed from `rxdAdapter`.
 *
 * Once we *do* create the backend, we eagerly call `LMSInitialize` and set
 * `cmi.core.exit='suspend'` (1.2) / `cmi.exit='suspend'` (2004) right away.
 * Rationale: in SCORM 1.2 `setProgressMeasure` is a no-op (the spec has no
 * `progress_measure`), so without an explicit bootstrap the LMS would see
 * zero API traffic until the user finishes a quiz or hits the Success page
 * — which means closing the tab mid-course leaves the LMS session in
 * "not attempted" forever. Mirrors the bootstrap that the Rustici RXD
 * driver performs on its own side for parity.
 */
function getBackend(): ScormAgainBackend | undefined {
  if (activeBackend) return activeBackend;
  if (!isScormAgainHostDetected()) return undefined;

  const parent = getParentWindow();
  if (!parent) return undefined;

  const version = readScormVersion();
  const api = createCrossFrameApi(parent);
  activeBackend = new ScormAgainBackend(version, api);
  activeBackend.bootstrapSession();
  return activeBackend;
}

/**
 * Always returns an adapter. Whether it actually talks to a scorm-again
 * launcher is decided per-call via {@link isScormAgainHostDetected} —
 * matches the auto-detection behavior of {@link createRxdAdapter}.
 */
export function createScormAgainAdapter(): LmsAdapter {
  const callDirect = (action: (b: ScormAgainBackend) => void) => {
    const backend = getBackend();
    if (!backend) return;
    action(backend);
  };

  const callInteraction = (
    type: 'choice' | 'true-false' | 'sequencing' | 'fill-in',
    args: InteractionArgs
  ) => {
    if (!isInteractionsEnabled()) return;
    callDirect(b => b.recordInteraction(type, args));
  };

  return {
    get name(): string {
      // Reported lazily so tests/diagnostics see the actual SCORM version
      // negotiated with the launcher rather than a placeholder captured at
      // boot. If the host isn't detected yet, fall back to a stable label.
      if (activeBackend) {
        return `scorm-again:${activeBackend.version}:cross-frame`;
      }
      return 'scorm-again:pending';
    },
    get isAvailable(): boolean {
      return isScormAgainHostDetected();
    },

    setProgressMeasure: value => callDirect(b => b.setProgressMeasure(value)),
    setScore: (score, maxScore, minScore) =>
      callDirect(b => b.setScore(score, maxScore, minScore)),
    setReachedEnd: () => callDirect(b => b.setReachedEnd()),
    setPassed: () => callDirect(b => b.setPassed()),
    concedeControl: () => callDirect(b => b.concedeControl()),

    recordMultipleChoiceInteraction: (...args) =>
      callInteraction('choice', args),
    recordTrueFalseInteraction: (...args) =>
      callInteraction('true-false', args),
    recordSequencingInteraction: (...args) =>
      callInteraction('sequencing', args),
    recordFillInInteraction: (...args) => callInteraction('fill-in', args),

    getLearnerLanguagePreference: async () => {
      const backend = getBackend();
      if (!backend) return '';
      try {
        return await backend.getLearnerLanguagePreference();
      } catch (error) {
        console.error(
          '[lms/scorm-again] getLearnerLanguagePreference failed:',
          error
        );
        return '';
      }
    }
  };
}

/** Test-only: tears down the active CrossFrameAPI (heartbeat + listeners). */
export function _destroyScormAgainBackendForTests(): void {
  scormAgainHostLatched = false;
  try {
    window.sessionStorage?.removeItem(SCORM_HOST_STORAGE_KEY);
    window.sessionStorage?.removeItem(SCORM_VERSION_STORAGE_KEY);
  } catch {
    /* noop */
  }
  if (!activeBackend) return;
  activeBackend.destroy();
  activeBackend = undefined;
}
